import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Image as ImageIcon, Loader2 } from 'lucide-react';

import { brand } from '@brand';
import { Icon as BrandIcon } from '@/lib/Icon';

import { PageHeader } from '@/components/app-shell';
import { useUploadDocument } from '@/lib/api/hooks';
import { cn } from '@/lib/cn';

/** Storefront: the upload refusal in the patient's terms, by the backend's
 *  code (`POST /me/documents/{id}/complete` → 422). Unknown codes keep the
 *  backend's own sentence. */
const ID_REFUSAL: Record<string, string> = {
  not_an_id:
    "This doesn't look like a government photo ID. Upload the front of your driver's license or state ID, or the photo page of your passport.",
  id_front_required:
    'This looks like the back of your ID. Upload the front, where your photo, full name and date of birth are.',
  unreadable_file:
    "We couldn't open or read this file. Try a clear photo (JPG or PNG) or a PDF, with all four corners in frame and no glare.",
};

interface IdUploadStepProps {
  onUploaded: () => void;
  onSkip: () => void;
  identityGateEnabled?: boolean;
}

// Government-ID capture between intakes and consent. The compounding
// pharmacy (TPH) keeps a copy on file. The patient can skip and pick
// it up later from the home task list — we don't block onboarding here.
//
// We intentionally omit the `capture=` attribute on the file input. On
// iOS/Android omitting it shows the OS sheet with "Take Photo", "Photo
// Library", and "Choose File" — which is the right UX without us
// building a camera. Adding `capture="environment"` would force-launch
// the camera and hide the photo library option.
export function IdUploadStep({
  onUploaded,
  onSkip,
  identityGateEnabled = false,
}: IdUploadStepProps) {
  const upload = useUploadDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewFeedback, setReviewFeedback] = useState<string | null>(null);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const uploadNowRef = useRef<HTMLButtonElement>(null);
  // Storefront: the skip dialog takes focus (its first button) when it
  // opens, so a keyboard or screen reader user lands inside it.
  useEffect(() => {
    if (brand.storefrontOnboarding && confirmSkip) uploadNowRef.current?.focus();
  }, [confirmSkip]);
  const errorRef = useRef<HTMLDivElement>(null);

  // A refusal is announced and brought into view above the sticky footer
  // (it rendered under the Continue bar on phones).
  useEffect(() => {
    if (!error || !brand.storefrontOnboarding) return;
    errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    errorRef.current?.focus({ preventScroll: true });
  }, [error]);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    setReviewFeedback(null);
    setFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (reviewFeedback) {
      onUploaded();
      return;
    }
    if (!file) return;
    setError(null);
    try {
      const result = await upload.mutateAsync({ file, purpose: 'id' });
      if (result.feedback) {
        setReviewFeedback(result.feedback);
        return;
      }
      onUploaded();
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      setError(
        (brand.storefrontOnboarding && ID_REFUSAL[code]) ||
          ((err as Error).message ?? 'Upload failed'),
      );
    }
  };

  const fileSizeLabel = useMemo(() => {
    if (!file) return null;
    if (file.size < 1024) return `${file.size} B`;
    if (file.size < 1024 * 1024) return `${(file.size / 1024).toFixed(1)} KB`;
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }, [file]);

  return (
    <div className="flex-1 flex flex-col">
      <PageHeader
        title="Government ID"
        description="Upload the complete front of your driver's license or state ID, or your passport photo page. Your photo, full name, and date of birth must be readable. The back alone is not sufficient. JPG, PNG, or PDF."
      />
      <div className="flex-1 px-15 md:px-40 pt-20 md:pt-40 pb-30 max-w-[44rem] md:max-w-[64rem] mx-auto w-full flex flex-col gap-20">
        {/* HEIC is listed because the backend converts it (pillow-heif), so
            Mac/desktop users can pick iPhone photos directly. Deliberately
            NOT image/* though — unlisted exotic formats stay blocked, and on
            iOS an explicit list keeps the photo-library picker behavior
            predictable. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
          className="hidden"
          onChange={handleChange}
        />

        <button
          type="button"
          onClick={handlePick}
          disabled={upload.isPending}
          className={cn(
            'flex flex-col items-center justify-center gap-10 py-40 px-20',
            'border-2 border-dashed border-line-default bg-[var(--surface-page)]',
            'hover:border-ink hover:bg-[var(--surface-sunken)]',
            'disabled:opacity-50',
          )}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="ID preview"
              className="max-h-[14rem] max-w-full object-contain"
            />
          ) : file ? (
            <FileText
              size={28}
              strokeWidth={1.5}
              className="text-ink-secondary"
              aria-hidden
            />
          ) : (
            <ImageIcon
              size={28}
              strokeWidth={1.5}
              className="text-ink-muted"
              aria-hidden
            />
          )}
          <div className="type-label-md text-ink">
            {file ? 'Replace photo' : 'Choose photo or PDF'}
          </div>
          {file ? (
            <div className="type-body-sm text-ink-secondary truncate max-w-full">
              {file.name}
              {fileSizeLabel ? ` · ${fileSizeLabel}` : ''}
            </div>
          ) : (
            <div className="type-body-sm text-ink-muted">
              Tap to open camera, photo library, or files
            </div>
          )}
        </button>

        {error && (
          <div
            ref={errorRef}
            role="alert"
            tabIndex={-1}
            className={cn(
              'type-body-sm text-danger',
              brand.storefrontOnboarding &&
                'type-body-md border border-danger bg-danger-wash px-15 py-12 leading-130 outline-none scroll-mb-[12rem]',
            )}
          >
            {error}
          </div>
        )}
        {reviewFeedback && (
          <div role="status" className="type-body-sm text-ink-secondary">
            {reviewFeedback}
          </div>
        )}
      </div>

      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-20 py-15 md:px-40 md:py-20 flex flex-col gap-10 items-center">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || upload.isPending}
          className="w-full flex items-center justify-between h-50 px-20 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50 max-w-[64rem] mx-auto"
        >
          {upload.isPending ? (
            <span className="flex items-center gap-10">
              <Loader2 size={14} className="animate-spin" /> Uploading and
              checking ID…
            </span>
          ) : (
            'Continue'
          )}
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-8"
          >
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
        <button
          type="button"
          onClick={() =>
            identityGateEnabled ? setConfirmSkip(true) : onSkip()
          }
          disabled={upload.isPending}
          className="type-body-sm text-ink-muted hover:text-ink underline-offset-2 hover:underline disabled:opacity-50"
        >
          Skip for now
        </button>
      </div>
      {confirmSkip && (
        <div
          className={cn(
            'fixed inset-0 z-dialog p-20 flex items-center justify-center',
            // Storefront: the cancel dialog's scrim (`bg-ink/55` builds to
            // nothing: ink is a CSS variable, which takes no opacity).
            brand.storefrontOnboarding ? 'bg-black/40 backdrop-blur-[6px]' : 'bg-ink/55',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-id-title"
          aria-describedby={brand.storefrontOnboarding ? 'skip-id-body' : undefined}
          onKeyDown={
            brand.storefrontOnboarding
              ? (e) => {
                  if (e.key === 'Escape') setConfirmSkip(false);
                }
              : undefined
          }
        >
          <section className="max-w-[48rem] w-full bg-page p-20 border border-line">
            <h2
              id="skip-id-title"
              className={brand.storefrontOnboarding ? 'type-title-md text-ink' : 'type-heading-md'}
            >
              Upload your ID later?
            </h2>
            {brand.storefrontOnboarding ? (
              <>
                <p id="skip-id-body" className="type-body-md mt-12">
                  That&rsquo;s fine. A provider checks your photo ID before reviewing your
                  order, so your review starts once it&rsquo;s in. We&rsquo;ll keep a reminder
                  on your Home screen.
                </p>
                <p className="type-body-sm text-ink-muted mt-10">
                  The provider decides whether treatment is right for you after reviewing
                  your answers and ID.
                </p>
              </>
            ) : (
              <>
                <p className="type-body-md mt-12">
                  Please upload your ID before treatment review. Your clinician
                  verifies your identity during prescribing. You can continue
                  account setup and upload your ID from the portal when you are
                  ready.
                </p>
                <p className="type-body-sm text-ink-muted mt-10">
                  {brand.appointmentsCopy
                    ? 'Your appointments remain available. A purchase does not authorize a prescription.'
                    : 'A purchase does not authorize a prescription.'}
                </p>
              </>
            )}
            <div className="flex flex-wrap gap-10 mt-20">
              {/* Storefront: uploading is the filled button (skipping delays
                  the provider's review); the house keeps its order. */}
              <button
                type="button"
                ref={uploadNowRef}
                className={
                  brand.storefrontOnboarding
                    ? 'bg-ink text-page px-15 py-10 type-label-sm'
                    : 'border border-line px-15 py-10 type-label-sm'
                }
                onClick={() => setConfirmSkip(false)}
              >
                Upload now
              </button>
              <button
                type="button"
                className={
                  brand.storefrontOnboarding
                    ? 'border border-line px-15 py-10 type-label-sm'
                    : 'bg-ink text-page px-15 py-10 type-label-sm'
                }
                onClick={onSkip}
              >
                Continue without ID
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
